import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import andOricon from "../../assets/icons/and-or-icon.svg";
import andIcon from "../../assets/icons/and-icon.svg";
import addCircleIcon from "../../assets/icons/plus-icon-magenta.svg";
import { ReactComponent as DeleteBinIcon } from "../../assets/icons/delete-bin.svg";
import { ReactComponent as CloneIcon } from "../../assets/icons/duplicate-icon-magenta.svg";
import "./DynamicNestedDropdownSegmentSection.scss";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { findFieldByPath } from "../../utils/findFieldByPath";
import { findTableForValue } from "../../utils/findTableForValue";
import FilterWrapSegmentSection from "./FilterWrapSegmentSection";
import { RecursiveDropdown } from "./RecursiveDropdown";
import CustomTooltip from "../../shared/components/custom-tooltip/CustomTooltip";
import { DropdownItem, DropdownMenuProps, ExpandedMap, OpenSubmenu } from "./segment-interface";

const STIRISTA_PREFIX = "Stirista-";

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  indexSection,
  controlName,
  dataDropdown,
  removeSection,
  cloneSection,
  // moveSection,
  removeDelete = false,
}) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const currentPathSplit = currentPath.split("/");

  const [submenuStack, setSubmenuStack] = useState<OpenSubmenu[]>([]);
  const cloneSectionTooltip = {
    alt: "Duplicate filter ",
    tooltip: "Duplicate filter ",
  };
  const deleteSectionTooltip = {
    alt: "delete filter",
    tooltip: "Delete filter",
  };
  const [showMenu, setShowMenu] = useState(false);
  const [expandedMap, setExpandedMap] = useState<ExpandedMap>({});
  const [activePath, setActivePath] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const [stateRemoveCondition, setStateRemoveCondition] = useState(null);

  useLayoutEffect(() => {
    // Fit submenu heights to the viewport:
    // - if a submenu is too tall, enable an internal scrollbar instead of scrolling the whole page
    // - keep the max-height consistent across deeper submenu levels
    if (!showMenu) return;
    const menuWrapperEl = menuRef.current;
    if (!menuWrapperEl) return;

    const applySubmenuScroll = () => {
      // Each `.dropdown__submenu` contains a `.dropdown` list (ul) inside.
      // We measure and constrain that list, because it is the element that actually scrolls.
      const submenuLists = menuWrapperEl.querySelectorAll<HTMLElement>(".dropdown__submenu .dropdown");
      const rootSubmenuList = submenuLists[0];
      if (!rootSubmenuList) return;

      // `visualViewport` is a better approximation of the visible viewport (e.g. macOS zoom / browser UI),
      // fall back to `innerHeight` if it is not available.
      const viewportHeightPx = window.visualViewport?.height ?? window.innerHeight;

      // The root submenu position relative to the viewport.
      const rootSubmenuRect = rootSubmenuList.getBoundingClientRect();

      // Maximum available space from the submenu's top edge down to the bottom of the viewport.
      const rootAvailableHeightPx = Math.max(120, viewportHeightPx - rootSubmenuRect.top - 4);

      // Natural content height (without any constraints).
      const rootContentHeightPx = rootSubmenuList.scrollHeight;

      // Base height limit (never exceed the viewport).
      const rootMaxHeightPx = Math.min(rootContentHeightPx, rootAvailableHeightPx);

      // If the root submenu already touches/reaches the viewport bottom, do not expand.
      // If there is extra space, allow a ~22% increase (still capped to the available viewport space).
      const rootTouchesViewportBottom = rootSubmenuRect.bottom >= viewportHeightPx - 1;
      const targetMaxHeightPx = rootTouchesViewportBottom
        ? rootMaxHeightPx
        : Math.min(rootAvailableHeightPx, Math.round(rootMaxHeightPx * 1.22));

      for (let i = 0; i < submenuLists.length; i++) {
        const submenuListEl = submenuLists[i];
        if (submenuListEl.scrollHeight > targetMaxHeightPx) {
          submenuListEl.style.maxHeight = `${targetMaxHeightPx}px`;
          submenuListEl.style.overflowY = "auto";
          submenuListEl.style.overscrollBehavior = "contain";
        } else {
          submenuListEl.style.maxHeight = "";
          submenuListEl.style.overflowY = "";
          submenuListEl.style.overscrollBehavior = "";
        }
      }
    };

    // Run after layout and on window resize.
    applySubmenuScroll();
    window.addEventListener("resize", applySubmenuScroll);
    return () => window.removeEventListener("resize", applySubmenuScroll);
  }, [showMenu, submenuStack]);

  const methods = useFormContext();

  const { control, getValues, setValue, watch } = methods;

  const { fields, append, remove, insert, replace } = useFieldArray({
    control,
    name: `${controlName}.${indexSection}.conditions`,
    keyName: "conditionId",
  });

  const backendData: DropdownItem[] = dataDropdown?.data || [];

  const sectionConditions = watch(`${controlName}.${indexSection}.conditions`) || [];
  const hasSelectedSurveyQuestion = sectionConditions.some((c: any) => {
    if (typeof c?.table !== "string" || !c.table.includes("entity_survey_table")) return false;
    if (c?.field !== "question") return false;
    if (Array.isArray(c?.value)) return c.value.length > 0 && Boolean(c.value[0]);
    return Boolean(c?.value);
  });

  const handleClick = (path: string[], ojbPram?: any) => {
    // Resolve the clicked leaf from the nested taxonomy path.
    const leaf = findFieldByPath(backendData, path);
    const { isUpdate, indexCondition } = ojbPram || {};

    if (path[path.length - 1] === "Segment") {
      setShowMenu(false);
      return;
    }

    if (!leaf || !leaf.type) return;

    const table = findTableForValue(backendData, leaf.backend_key);
    // Detect Stirista at selection time, so we can react immediately in the form.
    const isStiristaSelection =
      path.some((p) => typeof p === "string" && /stirista/i.test(p)) ||
      /stirista/i.test(leaf.label || "") ||
      /stirista/i.test(leaf.backend_key || "") ||
      /stirista/i.test(String(table || ""));

    if (isStiristaSelection) {
      // Auto-prefix segment name once user picks any Stirista attribute.
      const currentName = String(getValues("segmentName") || "");
      if (!currentName.startsWith(STIRISTA_PREFIX)) {
        const next = `${STIRISTA_PREFIX}${currentName}`.slice(0, 100);
        setValue("segmentName", next, { shouldDirty: true, shouldTouch: true });
      }
    }

    const ojb = {
      id: path.join("-"),
      path,
      selectedValue: leaf.label,
      type: leaf.type,
      field: leaf.value || "",
      // Persist this flag in condition payload to make later Stirista checks deterministic.
      isStirista: isStiristaSelection,
      selectOptions: leaf.options || [],
      value: "",
      logic: (fields?.[1] as any)?.logic
        ? (fields?.[1] as any)?.logic
        : fields?.length
        ? "AND"
        : "",
      listFilter: submenuStack[submenuStack?.length - 1]?.items,
      parentPath: submenuStack[submenuStack?.length - 1]?.parentPath,
      table,
      operator: "",
    };

    if (isUpdate) {
      const payloadIsUpdate = {
        ...getValues(`${controlName}.${indexSection}.conditions.${indexCondition}`),
        selectedValue: leaf.label,
        id: path.join("-"),
        path,
        field: leaf.value || "",
        type: leaf.type,
        // Keep Stirista metadata in sync when user changes an existing condition.
        isStirista: isStiristaSelection,
        value: "",
        operator: "",
        selectOptions: leaf.options || [],
      };

      insert(indexCondition, payloadIsUpdate);

      setStateRemoveCondition(indexCondition + 1); // Remove next item if exists
    } else {
      append(ojb);

      if (!leaf.children || leaf.children.length === 0) {
        setShowMenu(false);
        setExpandedMap({});
        setSubmenuStack([]);
        setActivePath([]);
      }
    }
  };


  const toggleExpand = (
    key: string,
    parentPath: string[],
    children: DropdownItem[],
    target: HTMLElement
  ) => {
    // Position child submenu next to the clicked item to preserve cascading menu UX.
    const containerRect = menuRef.current?.getBoundingClientRect();
    const itemRect = target.getBoundingClientRect();
    if (!containerRect || !itemRect) return;

    const left = itemRect.right - containerRect.left + 6;
    const depth = parentPath.length;

    setActivePath(key.split("/").map((_, i, arr) => arr.slice(0, i + 1).join("/")));

    setSubmenuStack((prev) => {
      const slicedStack = prev.slice(0, depth);
      return [
        ...slicedStack,
        {
          key,
          items: children,
          position: { left },
          parentPath: [...parentPath],
        },
      ];
    });

    setExpandedMap((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  const updateLogic = (index: number, logic: "AND" | "OR") => {
    const updatedFields = getValues(`${controlName}.${indexSection}.conditions`).map(
      (field: any) => {
        return { ...field, logic };
      }
    );

    replace(updatedFields);
  };

  const removeFilter = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setExpandedMap({});
        setSubmenuStack([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (stateRemoveCondition) {
      remove(stateRemoveCondition);

      setStateRemoveCondition(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateRemoveCondition]);

  const isAndOr = watch(`${controlName}.${indexSection}.conditions`)?.[1]?.logic;

  return (
    <>
      <div
        className={`${
          controlName === "exclusions" ? "!bg-[#FBF5F3]" : ""
        } dropdown__filter-section`}
      >
        <div className="title flex items-center justify-between" style={{ marginBottom: "18px" }}>
          <div className="flex items-center gap-2">
            {isAndOr === "OR" ? (
              <img src={andIcon} alt="and/or" />
            ) : (
              <img src={andOricon} alt="and" />
            )}

            <p className="font-lg font-[EnergyBold]">
              {controlName === "sections" ? "Include" : "Exclude"}{" "}
              {isAndOr === "OR" ? "ANY" : "ALL"} of these criteria
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* {((indexSection > 0 && controlName === "sections") || controlName === "exclusions") && (
            <div
              className="flex gap-2 font-[EnergyBold]"
              onClick={() => {
                moveSection?.();
              }}
              style={{ cursor: "pointer", color: "#F1181B" }}
            >
              Move Section
            </div>
            )} */}

            {((indexSection > 0 && controlName === "sections") || controlName === "exclusions") &&
              !removeDelete && (
                <div
                  className="section-action-btn"
                  onClick={() => {
                    removeSection();
                  }}
                >
                {deleteSectionTooltip && (
                  <CustomTooltip title={deleteSectionTooltip.tooltip}>
                    <DeleteBinIcon
                      className="deleteSection__icon"
                      role="img"
                      aria-label={deleteSectionTooltip.alt}
                    />
                  </CustomTooltip>
                )}
                </div>
              )}
            {( (controlName === "sections" || controlName === "exclusions") && !(currentPathSplit?.[1] === "segments")
            && (<div
                className="section-action-btn"
                onClick={() => {
                  cloneSection?.();
                }}
              >
              {cloneSectionTooltip && (
                <CustomTooltip title={cloneSectionTooltip.tooltip}>
                  <CloneIcon
                    className="cloneSection__icon"
                    role="img"
                    aria-label={cloneSectionTooltip.alt}
                  />
                </CustomTooltip>
              )}
              </div>
            ))}
          </div>
        </div>


        {fields?.map((filter: any, index) => (
          <Fragment key={filter.conditionId}>
            {index > 0 && (
              <div className="dropdown__switch">
                <div className="dropdown__switch__wrap">
                  <div
                    className={`dropdown__switch__wrap__and ${
                      filter.logic === "AND" ? "active" : ""
                    }`}
                    onClick={() => updateLogic(index, "AND")}
                  >
                    And
                  </div>
                  <div
                    className={`dropdown__switch__wrap__or ${
                      filter.logic === "OR" ? "active" : ""
                    }`}
                    onClick={() => updateLogic(index, "OR")}
                  >
                    Or
                  </div>
                </div>
              </div>
            )}
            <FilterWrapSegmentSection
              {...filter}
              key={filter.conditionId}
              indexSection={indexSection}
              controlName={controlName}
              indexCondition={index}
              onRemove={() => removeFilter(index)}
              // onClone={() => cloneFilter(index)}
              handleUpdateFilterCondition={(e: any, ojb?: any) => handleClick(e, ojb)}
              removeDelete={currentPathSplit?.[1] === "segments"}
            />
          </Fragment>
        ))}
        <div className={showMenu ? "dropdown__section" : ""}>
          <div className="dropdown__wrapper" ref={menuRef}>
            {submenuStack.map((submenu, index) => (
              <div
                key={submenu.key}
                className="dropdown__submenu"
                style={{
                  left: submenu.position.left,
                }}
              >
                <RecursiveDropdown
                  items={submenu.items}
                  expandedMap={expandedMap}
                  toggleExpand={toggleExpand}
                  activePath={activePath}
                  handleClick={handleClick}
                  parentPath={submenu.parentPath}
                  isItemDisabled={(item) =>
                    item?.backend_key === "entity_survey_table_answer" && !hasSelectedSurveyQuestion
                  }
                />
              </div>
            ))}
            <div className="dropdown__conditions-wrap">
              <div
                className="dropdown__conditions-wrap__add-condition"
                onClick={() => {
                  setShowMenu((prev) => {
                    const next = !prev;
                    if (next) {
                      setSubmenuStack([
                        {
                          key: "root",
                          items: backendData,
                          position: { left: 16 },
                          parentPath: [],
                        },
                      ]);
                    } else {
                      setSubmenuStack([]);
                    }
                    return next;
                  });
                  setExpandedMap({});
                }}
              >
                {currentPathSplit?.[1] !== "segments" && (
                  <>
                    <img src={addCircleIcon} alt="Add" />
                    <span>Add condition</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DropdownMenu;
